// Assessment PDF export — mirrors the email template using jsPDF
// Generates a properly branded A4 PDF without any backend call.

import { jsPDF } from 'jspdf';

type AssessmentPdfData = {
  subjectName: string;
  outcome: string;
  redFlagsDetected: boolean;
  redFlags: string[];
  observableSigns: string[];
  selfSymptoms: string[];
  incidentRef: string;
  createdAt: string;
};

// Brand colours (RGB)
const NAVY   = [10, 25, 41]   as const;
const TEAL   = [32, 201, 151] as const;
const RED    = [220, 38, 38]  as const;
const GREEN  = [22, 163, 74]  as const;
const AMBER  = [217, 119, 6]  as const;
const WHITE  = [255, 255, 255] as const;
const GRAY_BG = [248, 250, 252] as const;
const SLATE  = [51, 65, 85]   as const;
const MUTED  = [100, 116, 139] as const;
const YELLOW_BG  = [255, 251, 235] as const;
const YELLOW_BDR = [253, 230, 138] as const;
const RED_BG = [254, 242, 242] as const;
const RED_BDR = [254, 202, 202] as const;
const GREEN_BG  = [240, 253, 244] as const;
const GREEN_BDR = [187, 247, 208] as const;

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 14;
const CONTENT_W = PAGE_W - MARGIN * 2;
const BOTTOM_LIMIT = 272; // leave room for footer

// ── Helpers ──────────────────────────────────────────────────────────────────

const setFill = (doc: jsPDF, [r, g, b]: readonly [number, number, number]) =>
  doc.setFillColor(r, g, b);

const setStroke = (doc: jsPDF, [r, g, b]: readonly [number, number, number]) =>
  doc.setDrawColor(r, g, b);

const setColor = (doc: jsPDF, [r, g, b]: readonly [number, number, number]) =>
  doc.setTextColor(r, g, b);

const centeredText = (doc: jsPDF, text: string, y: number) => {
  const textW = doc.getTextWidth(text);
  doc.text(text, (PAGE_W - textW) / 2, y);
};

// Draw a rounded filled rect with an optional border
const filledBox = (
  doc: jsPDF,
  x: number, y: number, w: number, h: number,
  fill: readonly [number, number, number],
  border?: readonly [number, number, number],
  radius = 3,
) => {
  setFill(doc, fill);
  if (border) {
    setStroke(doc, border);
    doc.roundedRect(x, y, w, h, radius, radius, 'FD');
  } else {
    doc.roundedRect(x, y, w, h, radius, radius, 'F');
  }
};

// Wrapped text block — returns new y after drawing
const wrappedText = (
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxW: number,
  lineH: number,
): number => {
  const lines = doc.splitTextToSize(text, maxW);
  doc.text(lines, x, y);
  return y + lines.length * lineH;
};

// Bullet list inside a grey card — returns new y after the card
const findingsCard = (
  doc: jsPDF,
  title: string,
  items: string[],
  emptyText: string,
  y: number,
  isNA = false,
): number => {
  const padding = 4;
  const titleH = 6;
  const itemH = 5;
  const itemCount = isNA || items.length === 0 ? 1 : items.length;
  const boxH = padding + titleH + itemCount * itemH + padding;

  // Ensure we don't overflow the page
  if (y + boxH > BOTTOM_LIMIT) {
    doc.addPage();
    y = 20;
  }

  filledBox(doc, MARGIN, y, CONTENT_W, boxH, GRAY_BG);

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  setColor(doc, SLATE);
  doc.text(title.toUpperCase(), MARGIN + padding, y + padding + 3.5);

  // Items
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  setColor(doc, NAVY);
  const listStartY = y + padding + titleH + 1;

  if (isNA) {
    setColor(doc, MUTED);
    doc.setFont('helvetica', 'italic');
    doc.text('N/A — Assessment stopped at red flag stage', MARGIN + padding + 3, listStartY + 3.5);
  } else if (items.length === 0) {
    setColor(doc, MUTED);
    doc.setFont('helvetica', 'italic');
    doc.text(emptyText, MARGIN + padding + 3, listStartY + 3.5);
  } else {
    items.forEach((item, i) => {
      const itemY = listStartY + i * itemH + 3.5;
      setColor(doc, NAVY);
      doc.setFont('helvetica', 'normal');
      doc.text('•', MARGIN + padding + 1, itemY);
      const lines = doc.splitTextToSize(item, CONTENT_W - padding * 2 - 6);
      doc.text(lines, MARGIN + padding + 5, itemY);
    });
  }

  return y + boxH + 3;
};

// Coloured next-steps box — returns new y after
const nextStepsBox = (
  doc: jsPDF,
  redFlagsDetected: boolean,
  y: number,
): number => {
  if (y + 4 > BOTTOM_LIMIT) { doc.addPage(); y = 20; }

  const steps = redFlagsDetected
    ? [
        'Do NOT leave the person alone',
        'Call 000 (or local emergency) immediately if not already done',
        'Do NOT move the person unless in immediate danger',
        'Do NOT remove any helmet or protective equipment',
        'Monitor breathing and consciousness until help arrives',
      ]
    : [
        'Remove the person from play immediately — If in doubt, sit them out',
        'Have them assessed by a qualified medical professional within 24 hours',
        'Do NOT allow return to play on the same day',
        'Monitor for worsening symptoms over the next 24–48 hours',
        "Follow your sport's return-to-play protocol before resuming activity",
      ];

  const padding = 4;
  const titleH = 6;
  const lineH = 5;
  const boxH = padding + titleH + steps.length * lineH + padding;

  const fill = redFlagsDetected ? RED_BG : GREEN_BG;
  const border = redFlagsDetected ? RED_BDR : GREEN_BDR;
  const textColor = redFlagsDetected ? ([153, 27, 27] as const) : ([22, 101, 52] as const);
  const heading = redFlagsDetected ? 'Immediate Actions Required' : 'Recommended Next Steps';

  filledBox(doc, MARGIN, y, CONTENT_W, boxH, fill, border, 3);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  setColor(doc, textColor);
  doc.text(heading, MARGIN + padding, y + padding + 3.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  steps.forEach((step, i) => {
    const stepY = y + padding + titleH + i * lineH + 3;
    doc.text('•', MARGIN + padding + 1, stepY);
    const wrapped = doc.splitTextToSize(step, CONTENT_W - padding * 2 - 6);
    doc.text(wrapped, MARGIN + padding + 5, stepY);
  });

  return y + boxH + 3;
};

// ── Logo loader ───────────────────────────────────────────────────────────────
function loadLogoDataUrl(src: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(null); return; }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

// ── Watermark helper ──────────────────────────────────────────────────────────
type DocWithGState = {
  GState: (opts: { opacity: number }) => unknown;
  setGState: (gs: unknown) => void;
  saveGraphicsState: () => void;
  restoreGraphicsState: () => void;
};
function drawWatermark(doc: jsPDF, pageW: number, pageH: number, darkLogoDataUrl: string) {
  const wmW = pageW * 0.55;
  const wmH = wmW * (1279 / 1888);
  const wmX = (pageW - wmW) / 2;
  const wmY = (pageH - wmH) / 2;
  const d = doc as unknown as DocWithGState;
  d.saveGraphicsState();
  d.setGState(d.GState({ opacity: 0.07 }));
  doc.addImage(darkLogoDataUrl, 'PNG', wmX, wmY, wmW, wmH);
  d.restoreGraphicsState();
}

// ── Main export function ──────────────────────────────────────────────────────

export const downloadAssessmentAsPdf = async (data: AssessmentPdfData): Promise<void> => {
  const [logo, darkLogo] = await Promise.all([
    loadLogoDataUrl('/sy-logo-light.png'),
    loadLogoDataUrl('/sy-logo-dark.png'),
  ]);

  const {
    subjectName, outcome, redFlagsDetected,
    redFlags, observableSigns, selfSymptoms,
    incidentRef, createdAt,
  } = data;

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });

  // Draw watermark before header so it sits behind all content
  if (darkLogo) drawWatermark(doc, PAGE_W, PAGE_H, darkLogo);

  const dateStr = new Date(createdAt).toLocaleString('en-AU', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  const outcomeColor = redFlagsDetected
    ? RED
    : outcome.toLowerCase().includes('clear') ? GREEN : AMBER;

  // ── HEADER ────────────────────────────────────────────────────────────────
  filledBox(doc, 0, 0, PAGE_W, 48, NAVY, undefined, 0);

  // SY logo — top-LEFT of header (ratio 1888:1279 ≈ 1.476:1)
  if (logo) {
    const logoH = 12;
    const logoW = logoH * (1888 / 1279);
    doc.addImage(logo, 'PNG', MARGIN, 5, logoW, logoH);
  }

  // Brand name
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  setColor(doc, TEAL);
  centeredText(doc, 'SAFEGUARDING YOU', 13);

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  setColor(doc, WHITE);
  centeredText(doc, 'Concussion Assessment Report', 26);

  // Ref + date
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  setColor(doc, [148, 163, 184]);
  centeredText(doc, `Ref: ${incidentRef}  ·  ${dateStr}`, 35);

  // Charlie label
  doc.setFontSize(7.5);
  setColor(doc, [71, 85, 105]);
  centeredText(doc, 'Charlie — AI Concussion Co-Pilot', 43);

  let y = 56;

  // ── URGENCY BANNER ────────────────────────────────────────────────────────
  if (redFlagsDetected) {
    filledBox(doc, MARGIN, y, CONTENT_W, 13, RED, undefined, 3);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    setColor(doc, WHITE);
    centeredText(doc, '⚠  RED FLAGS DETECTED — Seek immediate medical attention. Call 000.', y + 8.5);
    y += 17;
  }

  // ── OUTCOME BADGE ─────────────────────────────────────────────────────────
  const badgeW = 60;
  const badgeX = (PAGE_W - badgeW) / 2;
  filledBox(doc, badgeX, y, badgeW, 9, outcomeColor, undefined, 4.5);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  setColor(doc, WHITE);
  centeredText(doc, outcome.toUpperCase(), y + 6.3);
  y += 15;

  // ── SUMMARY TABLE ─────────────────────────────────────────────────────────
  const rows = [
    ['Person Assessed', subjectName || 'Unknown'],
    ['Date / Time', dateStr],
    ['Outcome', outcome],
    ['Red Flags', redFlagsDetected ? 'YES — Detected' : 'None detected'],
  ];

  const labelW = 42;
  const valueX = MARGIN + labelW + 3;
  const rowH = 8;

  rows.forEach(([label, value], i) => {
    const rowY = y + i * rowH;

    // Alternating row background
    if (i % 2 === 0) {
      setFill(doc, [248, 250, 252]);
      doc.rect(MARGIN, rowY - 1, CONTENT_W, rowH, 'F');
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    setColor(doc, MUTED);
    doc.text(label, MARGIN + 2, rowY + 4.5);

    doc.setFont('helvetica', 'bold');
    setColor(doc, i === 2 ? outcomeColor : i === 3 ? (redFlagsDetected ? RED : GREEN) : NAVY);
    doc.text(value, valueX, rowY + 4.5);
  });

  y += rows.length * rowH + 8;

  // ── FINDINGS HEADING ──────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  setColor(doc, NAVY);
  doc.text('Assessment Findings', MARGIN, y);
  y += 6;

  // ── FINDINGS SECTIONS ─────────────────────────────────────────────────────
  if (redFlagsDetected) {
    y = findingsCard(doc, 'Red Flags Detected', redFlags, 'Red flags detected', y);
    y = findingsCard(doc, 'Observable Signs', [], '', y, true);
    y = findingsCard(doc, 'Self-Reported Symptoms', [], '', y, true);
  } else {
    y = findingsCard(doc, 'Red Flags', redFlags, 'No red flags detected', y);
    y = findingsCard(doc, 'Observable Signs', observableSigns, 'No observable signs noted', y);
    y = findingsCard(doc, 'Self-Reported Symptoms', selfSymptoms, 'No symptoms reported', y);
  }

  y += 2;

  // ── NEXT STEPS ────────────────────────────────────────────────────────────
  y = nextStepsBox(doc, redFlagsDetected, y);

  // ── DISCLAIMER ────────────────────────────────────────────────────────────
  if (y + 4 > BOTTOM_LIMIT) { doc.addPage(); y = 20; }

  const disclaimerText =
    'Important: Charlie is an information tool — not a medical device. This assessment does not replace professional medical evaluation. ' +
    'All suspected concussions must be assessed by a qualified medical professional. When in doubt, always err on the side of caution. ' +
    'Based on CRT6 and Australian Institute of Sport concussion guidelines.';

  const disclaimerLines = doc.splitTextToSize(disclaimerText, CONTENT_W - 10);
  const disclaimerH = disclaimerLines.length * 4.5 + 8;

  if (y + disclaimerH > BOTTOM_LIMIT) { doc.addPage(); y = 20; }
  filledBox(doc, MARGIN, y, CONTENT_W, disclaimerH, YELLOW_BG, YELLOW_BDR, 3);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  setColor(doc, [146, 64, 14]);
  doc.text('Important:', MARGIN + 4, y + 5.5);
  doc.setFont('helvetica', 'normal');
  const disclaimerBodyLines = doc.splitTextToSize(
    'Charlie is an information tool — not a medical device. This assessment does not replace professional medical evaluation. ' +
    'All suspected concussions must be assessed by a qualified medical professional. When in doubt, always err on the side of caution. ' +
    'Based on CRT6 and Australian Institute of Sport concussion guidelines.',
    CONTENT_W - 10,
  );
  doc.text(disclaimerBodyLines, MARGIN + 4, y + 10);
  y += disclaimerH + 4;

  // ── FOOTER ────────────────────────────────────────────────────────────────
  const footerY = PAGE_H - 10;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  setColor(doc, [148, 163, 184]);
  centeredText(doc, 'Sent by Charlie — Safeguarding You\'s Concussion Co-Pilot  ·  safeguardingyou.ai', footerY);

  // ── SAVE ──────────────────────────────────────────────────────────────────
  const timestamp = new Date().toISOString().slice(0, 10);
  doc.save(`charlie-assessment-${incidentRef !== 'PENDING' ? incidentRef + '-' : ''}${timestamp}.pdf`);
};
